import React, { useState, useEffect } from 'react';

const JobSeekers = () => {
  const [jobSeekers, setJobSeekers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    middleName: '',
    email: '',
    password: '',
    dateOfBirth: '',
    address: '',
    address2: '',
    race: '',
    disability: '',
    state: '',
    localGovernment: '',
    country: '',
    jobMode: '',
    jobDecision: '',
    jobDecisionSalary: '',
    job: '',
    gender: '',
    userType: 'jobSeeker',
    avatar: '',
    phoneNumber: '',
    whatsappNumber: '',
    jobAssignments: []
  });
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const token = localStorage.getItem('token');

  // Fetch jobseekers
  useEffect(() => {
    const fetchJobSeekers = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}admin/admin/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json();
        if (response.ok) {
          setJobSeekers(data.jobSeekers || []);
        } else {
          setError(data.message || 'Failed to fetch jobseekers');
        }
      } catch (err) {
        setError('Server error while fetching jobseekers');
        console.error('Fetch jobseekers error:', err);
      }
    };

    if (token) {
      fetchJobSeekers();
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      const url = editingId
        ? `${process.env.REACT_APP_API_URL}admin/admin/update-jobseeker/${editingId}`
        : `${process.env.REACT_APP_API_URL}admin/admin/create-jobseeker`;
      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess(data.message);
        setFormData({
          firstName: '',
          lastName: '',
          middleName: '',
          email: '',
          password: '',
          dateOfBirth: '',
          address: '',
          address2: '',
          race: '',
          disability: '',
          state: '',
          localGovernment: '',
          country: '',
          jobMode: '',
          jobDecision: '',
          jobDecisionSalary: '',
          job: '',
          gender: '',
          userType: 'jobSeeker',
          avatar: '',
          phoneNumber: '',
          whatsappNumber: '',
          jobAssignments: []
        });
        setEditingId(null);
        setShowForm(false);
        // Refresh jobseekers
        const dashboardResponse = await fetch(`${process.env.REACT_APP_API_URL}admin/admin/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const dashboardData = await dashboardResponse.json();
        if (dashboardResponse.ok) {
          setJobSeekers(dashboardData.jobSeekers || []);
        }
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Server error while saving jobseeker');
      console.error('Save jobseeker error:', err);
    }
  };

  const handleDeleteJobSeeker = async (id) => {
    if (!window.confirm('Are you sure you want to delete this jobseeker?')) return;
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}admin/admin/delete-jobseeker/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess(data.message);
        setJobSeekers(jobSeekers.filter(js => js._id !== id));
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Server error while deleting jobseeker');
      console.error('Delete jobseeker error:', err);
    }
  };

  const handleJobAssignmentChange = (index, field, value) => {
    const updatedAssignments = [...formData.jobAssignments];
    updatedAssignments[index] = { ...updatedAssignments[index], [field]: value };
    setFormData({ ...formData, jobAssignments: updatedAssignments });
  };

  const addJobAssignment = () => {
    setFormData({
      ...formData,
      jobAssignments: [...formData.jobAssignments, { jobTitle: '', status: 'pending', reason: '', assignedAt: '' }]
    });
  };

  const removeJobAssignment = (index) => {
    setFormData({
      ...formData,
      jobAssignments: formData.jobAssignments.filter((_, i) => i !== index)
    });
  };

  return (
    <div className="p-6 bg-white text-gray-800">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold text-gray-800">JobSeekers</h2>
        <button
          className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600 transition-colors"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancel' : 'Add JobSeeker'}
        </button>
      </div>
      {showForm && (
        <form onSubmit={handleSubmit} className="mb-6 bg-gray-100 p-6 rounded-lg shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input
              type="text"
              placeholder="First Name"
              value={formData.firstName}
              onChange={e => setFormData({ ...formData, firstName: e.target.value })}
              className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
              required
            />
            <input
              type="text"
              placeholder="Last Name"
              value={formData.lastName}
              onChange={e => setFormData({ ...formData, lastName: e.target.value })}
              className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
              required
            />
            <input
              type="text"
              placeholder="Middle Name"
              value={formData.middleName}
              onChange={e => setFormData({ ...formData, middleName: e.target.value })}
              className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <input
              type="email"
              placeholder="Email"
              value={formData.email}
              onChange={e => setFormData({ ...formData, email: e.target.value })}
              className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={formData.password}
              onChange={e => setFormData({ ...formData, password: e.target.value })}
              className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
              required={!editingId}
            />
            <input
              type="date"
              placeholder="Date of Birth"
              value={formData.dateOfBirth}
              onChange={e => setFormData({ ...formData, dateOfBirth: e.target.value })}
              className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <input
              type="text"
              placeholder="Address"
              value={formData.address}
              onChange={e => setFormData({ ...formData, address: e.target.value })}
              className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <input
              type="text"
              placeholder="Address 2"
              value={formData.address2}
              onChange={e => setFormData({ ...formData, address2: e.target.value })}
              className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <input
              type="text"
              placeholder="Race"
              value={formData.race}
              onChange={e => setFormData({ ...formData, race: e.target.value })}
              className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <input
              type="text"
              placeholder="Disability"
              value={formData.disability}
              onChange={e => setFormData({ ...formData, disability: e.target.value })}
              className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <input
              type="text"
              placeholder="State"
              value={formData.state}
              onChange={e => setFormData({ ...formData, state: e.target.value })}
              className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <input
              type="text"
              placeholder="Local Government"
              value={formData.localGovernment}
              onChange={e => setFormData({ ...formData, localGovernment: e.target.value })}
              className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <input
              type="text"
              placeholder="Country"
              value={formData.country}
              onChange={e => setFormData({ ...formData, country: e.target.value })}
              className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <input
              type="text"
              placeholder="Job Mode"
              value={formData.jobMode}
              onChange={e => setFormData({ ...formData, jobMode: e.target.value })}
              className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <input
              type="text"
              placeholder="Job Decision"
              value={formData.jobDecision}
              onChange={e => setFormData({ ...formData, jobDecision: e.target.value })}
              className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <input
              type="text"
              placeholder="Job Decision Salary"
              value={formData.jobDecisionSalary}
              onChange={e => setFormData({ ...formData, jobDecisionSalary: e.target.value })}
              className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <input
              type="text"
              placeholder="Job"
              value={formData.job}
              onChange={e => setFormData({ ...formData, job: e.target.value })}
              className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <select
              value={formData.gender}
              onChange={e => setFormData({ ...formData, gender: e.target.value })}
              className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">Select Gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
            <select
              value={formData.userType}
              onChange={e => setFormData({ ...formData, userType: e.target.value })}
              className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="jobSeeker">Job Seeker</option>
            </select>
            <input
              type="text"
              placeholder="Avatar URL"
              value={formData.avatar}
              onChange={e => setFormData({ ...formData, avatar: e.target.value })}
              className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <input
              type="text"
              placeholder="Phone Number"
              value={formData.phoneNumber}
              onChange={e => setFormData({ ...formData, phoneNumber: e.target.value })}
              className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <input
              type="text"
              placeholder="WhatsApp Number"
              value={formData.whatsappNumber}
              onChange={e => setFormData({ ...formData, whatsappNumber: e.target.value })}
              className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <div className="mt-4">
            <h3 className="text-lg font-semibold text-gray-800">Job Assignments</h3>
            {formData.jobAssignments.map((assignment, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-2">
                <input
                  type="text"
                  placeholder="Job Title"
                  value={assignment.jobTitle}
                  onChange={e => handleJobAssignmentChange(index, 'jobTitle', e.target.value)}
                  className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <select
                  value={assignment.status}
                  onChange={e => handleJobAssignmentChange(index, 'status', e.target.value)}
                  className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="pending">Pending</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="accepted">Accepted</option>
                  <option value="rejected">Rejected</option>
                </select>
                <input
                  type="text"
                  placeholder="Reason"
                  value={assignment.reason}
                  onChange={e => handleJobAssignmentChange(index, 'reason', e.target.value)}
                  className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <input
                  type="date"
                  placeholder="Assigned At"
                  value={assignment.assignedAt}
                  onChange={e => handleJobAssignmentChange(index, 'assignedAt', e.target.value)}
                  className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <button
                  type="button"
                  className="text-red-500 hover:text-red-600"
                  onClick={() => removeJobAssignment(index)}
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              className="mt-2 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
              onClick={addJobAssignment}
            >
              Add Job Assignment
            </button>
          </div>

          <button
            type="submit"
            className="mt-4 bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600 transition-colors"
          >
            {editingId ? 'Update' : 'Create'} JobSeeker
          </button>
          {success && <p className="mt-2 text-green-600">{success}</p>}
          {error && <p className="mt-2 text-red-600">{error}</p>}
        </form>
      )}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-200">
              <th className="p-2 text-left text-gray-700">Name</th>
              <th className="p-2 text-left text-gray-700">Email</th>
              <th className="p-2 text-left text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {jobSeekers.length > 0 ? (
              jobSeekers.map(jobSeeker => (
                <tr key={jobSeeker._id} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="p-2">{jobSeeker.firstName} {jobSeeker.lastName}</td>
                  <td className="p-2">{jobSeeker.email}</td>
                  <td className="p-2">
                    <button
                      className="text-orange-500 hover:text-orange-600 mr-2"
                      onClick={() => {
                        setFormData({
                          firstName: jobSeeker.firstName || '',
                          lastName: jobSeeker.lastName || '',
                          middleName: jobSeeker.middleName || '',
                          email: jobSeeker.email || '',
                          password: '', // Do not prefill password for security
                          dateOfBirth: jobSeeker.dateOfBirth || '',
                          address: jobSeeker.address || '',
                          address2: jobSeeker.address2 || '',
                          race: jobSeeker.race || '',
                          disability: jobSeeker.disability || '',
                          state: jobSeeker.state || '',
                          localGovernment: jobSeeker.localGovernment || '',
                          country: jobSeeker.country || '',
                          jobMode: jobSeeker.jobMode || '',
                          jobDecision: jobSeeker.jobDecision || '',
                          jobDecisionSalary: jobSeeker.jobDecisionSalary || '',
                          job: jobSeeker.job || '',
                          gender: jobSeeker.gender || '',
                          userType: jobSeeker.userType || 'jobSeeker',
                          avatar: jobSeeker.avatar || '',
                          phoneNumber: jobSeeker.phoneNumber || '',
                          whatsappNumber: jobSeeker.whatsappNumber || '',
                          jobAssignments: jobSeeker.jobAssignments || []
                        });
                        setEditingId(jobSeeker._id);
                        setShowForm(true);
                      }}
                    >
                      View & Edit
                    </button>
                    <button
                      className="text-red-500 hover:text-red-600"
                      onClick={() => handleDeleteJobSeeker(jobSeeker._id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="3" className="p-2 text-gray-600 text-center">
                  No jobseekers found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default JobSeekers;